
import React, { createContext, useContext, useCallback, useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import ConfirmModal from '../components/Common/ConfirmModal';

const NotificationContext = createContext();

export function NotificationProvider({ children }) {
  /**
   * 
   * @param {string} message 
   * @param {string} type 
   * @param {object} options 
   */
  const showNotification = useCallback((message, type = 'info', options = {}) => {
    const defaultOptions = {
      position: 'top-right',
      autoClose: 1500,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      ...options
    };

    switch (type) {
      case 'success':
        toast.success(message, defaultOptions);
        break;
      case 'error':
        toast.error(message, defaultOptions);
        break;
      case 'warning':
        toast.warning(message, defaultOptions);
        break;
      case 'info':
      default:
        toast.info(message, defaultOptions);
        break;
    }
  }, []);

  /**
   * Show success notification
   */
  const showSuccess = useCallback((message, options = {}) => {
    showNotification(message, 'success', options);
  }, [showNotification]);

  /**
   * Show error notification
   */
  const showError = useCallback((message, options = {}) => {
    showNotification(message, 'error', options);
  }, [showNotification]);

  /**
   * Show warning notification
   */
  const showWarning = useCallback((message, options = {}) => {
    showNotification(message, 'warning', options);
  }, [showNotification]);

  /**
   * Show info notification
   */
  const showInfo = useCallback((message, options = {}) => {
    showNotification(message, 'info', options);
  }, [showNotification]);

  /**
   * Show loading notification that can be updated
   */
  const showLoading = useCallback((message = 'Đang xử lý...') => {
    return toast.loading(message);
  }, []);

  /**
   * Update a loading notification
   */
  const updateNotification = useCallback((toastId, message, type = 'success') => {
    const options = {
      render: message,
      type,
      isLoading: false,
      autoClose: 1500
    };

    toast.update(toastId, options);
  }, []);

  /**
   * Dismiss notification by ID
   */
  const dismissNotification = useCallback((toastId) => {
    toast.dismiss(toastId);
  }, []);

 
  const dismissAll = useCallback(() => {
    toast.dismiss();
  }, []);

  /**
   * Show confirmation dialog using toast
   */
  const showConfirmation = useCallback((message, onConfirm, onCancel) => {
    const ConfirmationToast = ({ closeToast }) => (
      <div>
        <p>{message}</p>
        <div className="d-flex gap-2 mt-2">
          <button 
            className="btn btn-sm btn-primary"
            onClick={() => {
              onConfirm();
              closeToast();
            }}
          >
            Xác nhận
          </button>
          <button 
            className="btn btn-sm btn-secondary"
            onClick={() => {
              onCancel && onCancel();
              closeToast();
            }}
          >
            Hủy
          </button>
        </div>
      </div>
    );

    toast(<ConfirmationToast />, {
      autoClose: false,
      closeOnClick: false,
      closeButton: false
    });
  }, []);

  const [confirmState, setConfirmState] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: null,
    variant: 'primary',
    loading: false
  });

  // Dọn dẹp class body của Bootstrap khi đóng confirm modal
  useEffect(() => {
    if (!confirmState.show) {
      const cleanup = () => {
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
      };
      const timer = setTimeout(cleanup, 100);
      return () => clearTimeout(timer);
    }
  }, [confirmState.show]);

  /**
   * Show a premium confirmation modal
   */
  const showConfirm = useCallback(({ 
    title = 'Xác nhận', 
    message = 'Bạn có chắc chắn muốn thực hiện hành động này?', 
    onConfirm, 
    variant = 'primary' 
  }) => {
    setConfirmState({
      show: true,
      title,
      message,
      variant,
      onConfirm: async () => {
        // Đóng modal confirm ngay lập tức
        setConfirmState(prev => ({ ...prev, show: false, loading: false }));
        
        // Dọn dẹp class body
        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';

        try {
          if (onConfirm) await onConfirm();
        } catch (error) {
          console.error("Error executing confirm action:", error);
        }
      },
      loading: false
    });
  }, []);

  const contextValue = {
    showNotification,
    showConfirm,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    showLoading,
    updateNotification,
    dismissNotification,
    dismissAll,
    showConfirmation
  };

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <ConfirmModal 
        show={confirmState.show}
        onHide={() => setConfirmState(prev => ({ ...prev, show: false }))}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        variant={confirmState.variant}
        loading={confirmState.loading}
      />
    </NotificationContext.Provider>
  );
}

/**
 * Hook to use notification context
 */
export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
}

export default NotificationContext;